/**
 * BucketWorkflowPage - Independent Execution Workspace
 * Trello-like Kanban board for confirmed campaign plans
 * 
 * Key Features:
 * - Confirmed plans are independent snapshots (don't depend on original Plan)
 * - Full generation path stored (Steps 0-3)
 * - Delete confirmed plans/campaigns without affecting original Plan
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { 
  Loader2, Globe, Plus, ArrowLeft, Inbox, Palette, Play, 
  CheckCircle, Archive, RefreshCw, Target, Users, Sparkles,
  TrendingUp, Star, Trash2, MoreVertical, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { bucketWorkflowApi } from './api';
import CampaignCard from './CampaignCard';
import CampaignDetailDrawer from './CampaignDetailDrawer';

const API = process.env.REACT_APP_BACKEND_URL;

// Column configuration
const COLUMNS = [
  { id: 'new', label: 'New', icon: Inbox, color: 'bg-slate-100 border-slate-300' },
  { id: 'landing_personalized', label: 'Landing Page Personalized', icon: Palette, color: 'bg-blue-50 border-blue-300' },
  { id: 'running', label: 'Running', icon: Play, color: 'bg-green-50 border-green-300' },
  { id: 'completed', label: 'Completed', icon: CheckCircle, color: 'bg-purple-50 border-purple-300' },
  { id: 'archived', label: 'Archived', icon: Archive, color: 'bg-gray-100 border-gray-300' }
];

export default function BucketWorkflowPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [confirmedPlans, setConfirmedPlans] = useState([]);
  const [assets, setAssets] = useState([]);
  const [selectedAssetId, setSelectedAssetId] = useState(searchParams.get('asset_id') || '');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draggedCampaign, setDraggedCampaign] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  
  // Delete confirmation state (replaces native confirm() which is blocked in sandboxed iframes)
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null, name: '' });
  
  // Load assets and all campaigns (flattened from confirmed plans)
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Load assets (non-blocking)
      try {
        const assetsRes = await axios.get(`${API}/api/assets`, { headers });
        setAssets(assetsRes.data.filter(a => a.status === 'ready'));
      } catch (assetErr) {
        console.warn('Failed to load assets for filter:', assetErr);
      }
      
      // Load all buckets with campaigns in a single optimized request
      const bucketsData = await bucketWorkflowApi.getBucketsWithCampaigns(selectedAssetId || null);
      setConfirmedPlans(bucketsData);
      
      // Flatten campaigns from all buckets (data already includes campaigns)
      const allCampaigns = [];
      for (const bucket of bucketsData) {
        const bucketId = bucket.bucket_id || bucket.confirmed_plan_id;
        if (bucket.campaigns) {
          bucket.campaigns.forEach(c => {
            allCampaigns.push({
              ...c,
              // Add plan context to each campaign
              bucket_id: bucketId,
              confirmed_plan_id: bucketId,
              plan_id: bucket.plan_id || bucket.source_plan_id,
              plan_name: bucket.title || bucket.plan_name,
              asset_id: bucket.asset_id,
              asset_name: bucket.asset_name,
              status: bucket.status,
              bucket_status: bucket.status,
              // Include snapshot path for display
              step0_goal_offer: bucket.step0_goal_offer,
              step1_personas: bucket.step1_personas,
              step2_segments: bucket.step2_segments,
              step3_constraints: bucket.step3_constraints
            });
          });
        }
      }
      
      setCampaigns(allCampaigns);
    } catch (err) {
      console.error('Failed to load campaigns:', err);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [selectedAssetId]);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Handle asset filter change
  const handleAssetChange = (value) => {
    const newAssetId = value === 'all' ? '' : value;
    setSelectedAssetId(newAssetId);
    if (newAssetId) {
      setSearchParams({ asset_id: newAssetId });
    } else {
      setSearchParams({});
    }
  };
  
  // Handle drag start
  const handleDragStart = (e, campaign) => {
    setDraggedCampaign(campaign);
    e.dataTransfer.effectAllowed = 'move';
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };
  
  // Handle drag end
  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedCampaign(null);
    setDragOverColumn(null);
  };
  
  // Handle drag over
  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  };
  
  // Handle drag leave
  const handleDragLeave = () => {
    setDragOverColumn(null);
  };
  
  // Handle drop - update the confirmed plan status (affects all campaigns in that plan)
  const handleDrop = async (e, columnId) => {
    e.preventDefault();
    setDragOverColumn(null);
    
    if (!draggedCampaign || draggedCampaign.status === columnId) {
      setDraggedCampaign(null);
      return;
    }
    
    const oldStatus = draggedCampaign.status;
    const newStatus = columnId;
    const bucketId = draggedCampaign.bucket_id || draggedCampaign.confirmed_plan_id;
    
    // Optimistic update - update all campaigns from the same plan
    setCampaigns(prev => prev.map(c => 
      (c.bucket_id === bucketId || c.confirmed_plan_id === bucketId)
        ? { ...c, status: newStatus }
        : c
    ));
    
    try {
      await bucketWorkflowApi.updateBucketStatus(bucketId, newStatus);
      toast.success(`Moved to ${COLUMNS.find(c => c.id === newStatus)?.label}`);
    } catch (err) {
      // Revert on error
      setCampaigns(prev => prev.map(c => 
        (c.bucket_id === bucketId || c.confirmed_plan_id === bucketId)
          ? { ...c, status: oldStatus }
          : c
      ));
      toast.error('Failed to update status');
    }
    
    setDraggedCampaign(null);
  };
  
  // Handle card click
  const handleCardClick = (campaign) => {
    setSelectedCampaign(campaign);
    setDrawerOpen(true);
  };
  
  // Handle delete individual campaign (independent - doesn't affect original Plan)
  const handleDeleteCampaign = async (campaignId, campaignName = '') => {
    if (!campaignId) {
      toast.error('Campaign ID not found');
      return;
    }
    // Open confirmation dialog instead of native confirm()
    setDeleteDialog({ open: true, type: 'campaign', id: campaignId, name: campaignName });
  };
  
  // Actually perform the campaign deletion after confirmation
  const confirmDeleteCampaign = async () => {
    const campaignId = deleteDialog.id;
    setDeleteDialog({ open: false, type: null, id: null, name: '' });
    
    try {
      await bucketWorkflowApi.deleteConfirmedCampaign(campaignId);
      // Filter out the deleted campaign
      setCampaigns(prev => prev.filter(c => 
        c.bucket_campaign_id !== campaignId && 
        c.confirmed_campaign_id !== campaignId &&
        c.campaign_id !== campaignId
      ));
      toast.success('Campaign removed from Bucket List');
      
      // Close drawer if the deleted campaign was being viewed
      if (selectedCampaign && 
          (selectedCampaign.bucket_campaign_id === campaignId || 
           selectedCampaign.confirmed_campaign_id === campaignId)) {
        setDrawerOpen(false);
        setSelectedCampaign(null);
      }
    } catch (err) {
      console.error('Delete campaign error:', err);
      toast.error('Failed to delete campaign');
    }
  };
  
  // Handle delete entire confirmed plan (independent - doesn't affect original Plan)
  const handleDeletePlan = async (bucketId, planName = '') => {
    // Open confirmation dialog instead of native confirm()
    setDeleteDialog({ open: true, type: 'plan', id: bucketId, name: planName });
  };
  
  // Actually perform the plan deletion after confirmation
  const confirmDeletePlan = async () => {
    const bucketId = deleteDialog.id;
    setDeleteDialog({ open: false, type: null, id: null, name: '' });
    
    try {
      await bucketWorkflowApi.deleteBucket(bucketId);
      setCampaigns(prev => prev.filter(c => 
        c.bucket_id !== bucketId && c.confirmed_plan_id !== bucketId
      ));
      setConfirmedPlans(prev => prev.filter(p => 
        p.bucket_id !== bucketId && p.confirmed_plan_id !== bucketId
      ));
      toast.success('Confirmed plan removed from Bucket List');
    } catch (err) {
      console.error('Delete plan error:', err);
      toast.error('Failed to delete plan');
    }
  };
  
  // Handle delete confirmation
  const handleConfirmDelete = () => {
    if (deleteDialog.type === 'campaign') {
      confirmDeleteCampaign();
    } else if (deleteDialog.type === 'plan') {
      confirmDeletePlan();
    }
  };
  
  // Get campaigns for a column
  const getCampaignsForColumn = (columnId) => {
    return campaigns.filter(c => c.status === columnId);
  };
  
  return (
    <div className="min-h-screen bg-slate-50" data-testid="bucket-workflow-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-[1800px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/dashboard')}
                className="text-slate-500"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-semibold font-['Outfit']">Bucket List</h1>
                <p className="text-sm text-slate-500">Independent execution workspace for confirmed campaigns</p>
              </div>
            </div>
            
            {/* Asset Filter */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Globe className="w-4 h-4" />
                <span>Filter by Asset:</span>
              </div>
              <Select value={selectedAssetId || 'all'} onValueChange={handleAssetChange}>
                <SelectTrigger className="w-[200px]" data-testid="asset-filter">
                  <SelectValue placeholder="All Assets" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Assets</SelectItem>
                  {assets.map(asset => (
                    <SelectItem key={asset.id} value={asset.id}>
                      {asset.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={loadData}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content - Kanban Board */}
      <main className="max-w-[1800px] mx-auto p-6">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {COLUMNS.map(column => {
              const columnCampaigns = getCampaignsForColumn(column.id);
              const Icon = column.icon;
              const isDragOver = dragOverColumn === column.id;
              
              return (
                <div 
                  key={column.id}
                  className={`flex-shrink-0 w-72 rounded-lg border-2 transition-colors ${
                    isDragOver 
                      ? 'border-purple-400 bg-purple-50' 
                      : `${column.color} border-dashed`
                  }`}
                  onDragOver={(e) => handleDragOver(e, column.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, column.id)}
                  data-testid={`column-${column.id}`}
                >
                  {/* Column Header */}
                  <div className="p-3 border-b border-slate-200 bg-white/80 rounded-t-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-slate-600" />
                        <span className="font-medium text-sm text-slate-700">{column.label}</span>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {columnCampaigns.length}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Column Content */}
                  <div className="p-2 min-h-[400px] space-y-2">
                    {columnCampaigns.length === 0 ? (
                      <div className="text-center py-8 text-slate-400 text-sm">
                        {column.id === 'new' 
                          ? 'Confirm campaigns from a Plan to add here'
                          : 'Drag campaigns here'}
                      </div>
                    ) : (
                      columnCampaigns.map(campaign => (
                        <div
                          key={campaign.bucket_campaign_id || campaign.confirmed_campaign_id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, campaign)}
                          onDragEnd={handleDragEnd}
                        >
                          <CampaignCard
                            campaign={campaign}
                            onClick={() => handleCardClick(campaign)}
                            onDelete={() => handleDeleteCampaign(
                              campaign.bucket_campaign_id || campaign.confirmed_campaign_id,
                              campaign.name || 'this campaign'
                            )}
                            isDragging={draggedCampaign?.bucket_campaign_id === campaign.bucket_campaign_id || 
                                       draggedCampaign?.confirmed_campaign_id === campaign.confirmed_campaign_id}
                          />
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Empty State */}
        {!loading && campaigns.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-purple-100 rounded-full flex items-center justify-center">
              <Inbox className="w-8 h-8 text-purple-500" />
            </div>
            <h3 className="font-semibold text-lg mb-2">No Confirmed Campaigns Yet</h3>
            <p className="text-slate-500 mb-4">
              Confirm campaigns from a Plan to add them here as independent snapshots
            </p>
            <Button onClick={() => navigate('/potential-campaigns')}>
              <Plus className="w-4 h-4 mr-2" />
              Go to Plans
            </Button>
          </div>
        )}
      </main>
      
      {/* Detail Drawer */}
      <CampaignDetailDrawer
        campaign={selectedCampaign}
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedCampaign(null);
        }}
        onDeleteCampaign={handleDeleteCampaign}
      />
      
      {/* Delete Confirmation Dialog (replaces native confirm() blocked in sandboxed iframes) */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, type: null, id: null, name: '' })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteDialog.type === 'plan' ? 'Delete Confirmed Plan?' : 'Delete Campaign?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDialog.type === 'plan' 
                ? `This will delete "${deleteDialog.name || 'this plan'}" and all its campaigns from your Bucket List. The original Plan is not affected.`
                : `This will remove "${deleteDialog.name || 'this campaign'}" from your Bucket List. The original Plan is not affected.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
